import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Navbar from "./components/Navbar";
import Popup from "./components/Popup";
import { useLocation, useNavigate } from "react-router-dom";
import useVoiceDuration from "./DurationTimer";
import LoadingScreen from "./components/LoadingScreen";
import WordCloudSection from "./components/WordCloudSection";
import RecordingControls from "./components/RecordingControls";
import TranscriptionSection from "./components/TranscriptionSection";
import ConversationPercentage from "./components/ConversationPercentage";
import FooterSection from './components/FooterSection';
import ChangingWord from "./components/ChanginWord";
import RealTimeOscillator from "./components/RealTimeOscillator ";

function MetabolismWordCloud({ name = "", positiveWords = [], department }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [wordCloudData, setWordCloudData] = useState([]);
  const [translatedWords, setTranslatedWords] = useState({}); 
  const [translatedSentences, setTranslatedSentences] = useState({}); // New state for translated words
  const { patientId } = location.state;
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recognition, setRecognition] = useState(null);
  const [error, setError] = useState(null);
  const [wordPercentage, setWordPercentage] = useState();
  const [negativeOnlyMessage, setNegativeOnlyMessage] = useState(false);
  const [positiveSentences, setPositiveSentences] = useState([]);
  const [hasPositiveWords, setHasPositiveWords] = useState(true);
  const [positive_pred, setPositivePred] = useState([]);
  const [negative_pred, setNegativePred] = useState([]);  
  const [sentiment, setSentiment] = useState(); // Add sentiment state

  const closePopup = () => {
    setNegativeOnlyMessage(false);
    // Clear the transcript
    setTranscript("");
  };

  const { voiceDuration, startTimer, stopTimer } = useVoiceDuration();
  // const endOfScreenRef = useRef(null);
  const trancscriptionSection = useRef(null);

  const [loading, setLoading] = useState(true);
  // const size = [400, 400];

  const isFetchingDataRef = useRef(false); // Ref to track if data fetching is in progress

  useEffect(() => {
    const fetchWordCloudData = async () => {
      if (isFetchingDataRef.current) {
        return; // If data fetching is already in progress, return
      }

      isFetchingDataRef.current = true; // Set data fetching to true

      try {
        const storedData = localStorage.getItem("wordCloudData");
        if (storedData) {
          // Handle cached data
          const {
            positive_pred,
            negative_pred,
            positive_sentences,
            has_positive_words,
            translated_words, // Add this line to get translated words from storage
            translated_sentences

          } = JSON.parse(storedData);
          const wordCloudWords = [...positive_pred, ...negative_pred];
          
          setWordCloudData(wordCloudWords);
          setPositiveSentences(positive_sentences);
          setPositivePred(positive_pred);
          setNegativePred(negative_pred);
          setHasPositiveWords(has_positive_words);
          setTranslatedWords(translated_words); // Set translated words in state
          setTranslatedSentences(translated_sentences);
          setLoading(false);
          
          isFetchingDataRef.current = false; // Reset data fetching status
        } else {
          // Fetch data from the API
          const {
            age,
            cultural_background,
            duration_of_diabetes,
            disposition,
            historical_glycemic,
            reason_for_consultation,
            patientId,
            fpg,
            ppg,
            hba1c,
          } = location.state;

          const response = await axios.get(
            `https://testone.conversationaily.ai/api/diabetes/generate_wordcloud`,
            // "https://testone.conversationaily.ai/api/wordcloud",

            {
              params: {
                age: age,
                cultural_background: cultural_background,
                duration_of_diabetes: duration_of_diabetes,
                disposition: disposition,
                historical_glycemic: historical_glycemic,
                reason_for_consultation: reason_for_consultation,
                patientId: patientId,
                fpg: fpg,
                ppg: ppg,
                hba1c: hba1c,
              },
            }
          );
          const {
            positive_pred,
            negative_pred,
            positive_sentences,
            has_positive_words,
            translated_words,
            translated_sentences

        
          } = response.data;
          const wordCloudWords = [...positive_pred, ...negative_pred];
          setPositivePred(positive_pred);
          setNegativePred(negative_pred);
          setWordCloudData(wordCloudWords);
          setPositiveSentences(positive_sentences);
          setHasPositiveWords(has_positive_words);
          setTranslatedWords(translated_words); 
          setTranslatedSentences(translated_sentences);
          setLoading(false);
          isFetchingDataRef.current = false; // Reset data fetching status
          localStorage.setItem("wordCloudData", JSON.stringify(response.data));
        }
      } catch (error) {
        console.error("Error fetching word cloud data:", error);
        setError(error);
        isFetchingDataRef.current = false; // Reset data fetching status in case of error
      }
    };

    fetchWordCloudData();
  }, [location.state]); // Only depend on location.state for initial fetch
  
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error("Speech recognition not supported");
      return;
    }
    const newRecognition = new SpeechRecognition();
    newRecognition.continuous = true;
    newRecognition.interimResults = false; // Ensure only final results are captured
    setRecognition(newRecognition);

    return () => {
      newRecognition.stop();
    };
  }, []);

  useEffect(() => {
    if (recognition) {
      console.log(recording);
      recognition.onresult = (event) => {
        let newTranscript = "";
        let finalTranscript = transcript;
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            newTranscript += event.results[i][0].transcript + " ";
          }
        }
        console.log(newTranscript);
        if (newTranscript.trim() !== "") {
          setTranscript((prevTranscript) => {
            const updatedTranscript = prevTranscript + newTranscript;
            console.log(updatedTranscript);
            if (recording) {
              let positiveWordsExtracted = []
              let negativeWordsExtracted = []
              const storedData = localStorage.getItem("wordCloudData");
      
              if (storedData) {
               const parsedData = JSON.parse(storedData);

               const {
                  positive_pred = [],
                  negative_pred = []
                } = parsedData;

               // Extract only text values from positive_pred
               positiveWordsExtracted = positive_pred.map((word) => word.text);

               negativeWordsExtracted = negative_pred.map((word) => word.text);
                // positiveWords = positiveWordsExtracted
              }

              calculateRealTimeSentiment(updatedTranscript,positiveWordsExtracted,negativeWordsExtracted);
            }
            return updatedTranscript;
          });
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setError(event.error);
      };
    }
  }, [recognition, recording, transcript]);


  const toggleRecording = async () => {
    if (!recording) {
      // const consent = window.confirm("By starting recording, your conversation will be saved. Are you sure you want to continue?");
      // if (!consent) {
      //   return;
      // }
      recognition.start();
      setRecording(true);
      startTimer(); // Start the voice duration timer
    } else {
      recognition.stop();
      stopTimer(); // Stop the voice duration timer
      setRecording(false);
      calculateWordPercentage(transcript, positiveWords);
      trancscriptionSection.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const calculateRealTimeSentiment = async (transcription, positiveWords, negativeWords) => {
    try {
      console.log(transcription);
      const response = await axios.post(
        `https://testone.conversationaily.ai/api/diabetes/calculate_word_percentage`,
        {
          transcription: transcription,positiveWords: positiveWords,negativeWords: negativeWords
        }
      );
      const sentimentValue = response.data.overall_percentage;
      console.log(sentimentValue);
      setSentiment(sentimentValue);
    } catch (error) {
      console.error("Error calculating word percentage:", error);
    }
  };

  const handleSaveAndGoHome = () => {
    // handleSendSMS();
    const finalDuration = voiceDuration; // Store the final duration
    saveTranscript(transcript, finalDuration);
    navigate("/metabolism");
  };

  const formatDuration = ({ hours, minutes, seconds }) => {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const calculateWordPercentage = async (transcription, positiveWords, negativeWords) => {
    try {
      let positiveWordsExtracted = []
      let negativeWordsExtracted = []
      const storedData = localStorage.getItem("wordCloudData");
      
      if (storedData) {
        const parsedData = JSON.parse(storedData);

        const {
          positive_pred = [],
          negative_pred = []
        } = parsedData;

        // Extract only text values from positive_pred
        positiveWordsExtracted = positive_pred.map((word) => word.text);
        positiveWords = positiveWordsExtracted

        negativeWordsExtracted = negative_pred.map((word) => word.text);
        negativeWords = negativeWordsExtracted
      }
      const response = await axios.post(
        `https://testone.conversationaily.ai/api/diabetes/calculate_word_percentage`,
        // "https://testone.conversationaily.ai/api/calculate_word_percentage",

        {
          transcription: transcription, positiveWords: positiveWords, negativeWords: negativeWords
        }
      );
      setWordPercentage(response.data.overall_percentage);

      // Check if there are no positive words
      const noPositiveWords = response.data.positive_word_count === 0;

      if (noPositiveWords) {
        setNegativeOnlyMessage(true);
      } else {
        setNegativeOnlyMessage(false);
      }
    } catch (error) {
      console.error("Error calculating word percentage:", error);
    }
  };

  const saveTranscript = async (transcript, duration) => {
    try {
     const doctorId = parseInt(localStorage.getItem("doctorId"));
      console.log("Doctor ID being sent:", doctorId);
      await axios.post(
        `https://testone.conversationaily.ai/api/diabetes/save_transcription`,
        {
          patientId: patientId,
          doctor_id: doctorId,
          transcript: transcript,
          duration: duration,
        }
      );
      // await axios.post("https://testone.conversationaily.ai/api/save_transcript", {
      //   patientId: patientId,
      //   transcript: transcript,
      //   duration: duration,
      // });
    } catch (error) {
      console.error("Error saving transcript:", error);
    }
  };



  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && (
          <div className="min-h-screen bg-white">

            <Navbar name={name} />

            <div className="wordcloudcontainer mx-auto">
              <div className="my-4 mx-4 flex justify-between items-center">

              </div>
              <WordCloudSection
                positiveSentences={positiveSentences}
                positiveWords={positive_pred}
                negativeWords={negative_pred}
                translatedWords={translatedWords}
                translatedSentences={translatedSentences}
              />

              <div>
                {negativeOnlyMessage && (
                  <Popup
                    message="No positive words were found in the transcript. Please try to use more positive words in your conversation."
                    onClose={closePopup}
                  />
                )}
<RealTimeOscillator sentiment={sentiment} isRecording="true" />

                <RecordingControls
                  recording={recording}
                  toggleRecording={toggleRecording}
                  voiceDuration={voiceDuration}
                  formatDuration={formatDuration}
                  handleSaveAndGoHome={handleSaveAndGoHome}
                  sentiment={sentiment}
                />

<div ref={trancscriptionSection}>
                <TranscriptionSection
                  transcript={transcript}
                  setTranscript={setTranscript}
                />
              </div>


              <ConversationPercentage
                wordPercentage={wordPercentage}
                handleSaveAndGoHome={handleSaveAndGoHome}
              />

                {/* <div ref={endOfScreenRef}></div> */}
              </div>
            </div>
          </div>
      )}
    </>
  );
}

export default MetabolismWordCloud;
